import { useState } from "react";
import axios from "axios";
import { Send, CheckCircle2, Loader2, Mail, Instagram } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "Coaching Inquiry",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", email: "", subject: "Coaching Inquiry", message: "" });
    } catch (err) {
      setStatus("error");
      setError(
        err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          "Something went wrong. Try again."
      );
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-36 bg-gradient-to-b from-[#0A0A0A] to-[#1a0d2b]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <div className="text-[#D4AF37] font-accent text-xs uppercase tracking-[0.3em] mb-3">
              Get In Touch
            </div>
            <h2 className="font-display text-5xl md:text-7xl text-white leading-[0.9] mb-6">
              Ready To
              <br />
              <span className="text-[#DC2626]">Work?</span>
            </h2>
            <p className="text-neutral-400 text-lg leading-relaxed mb-10 max-w-md">
              Coaching inquiries, brand collabs, sponsorship, or content questions —
              drop a message and I'll respond personally within 48 hours.
            </p>

            <div className="space-y-5">
              <a
                href="https://instagram.com/OmniMuscle_Bodybuilding"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram"
                className="flex items-center gap-4 group"
              >
                <div className="w-12 h-12 bg-[#4C1D95] flex items-center justify-center group-hover:bg-[#D4AF37] group-hover:text-black transition-colors">
                  <Instagram size={22} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-neutral-500">
                    Instagram
                  </div>
                  <div className="text-white font-semibold group-hover:text-[#D4AF37] transition-colors">
                    @OmniMuscle_Bodybuilding
                  </div>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#4C1D95] flex items-center justify-center">
                  <Mail size={22} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-neutral-500">
                    Email
                  </div>
                  <a
                    data-testid="contact-email-link"
                    href="mailto:CoachOmniMuscle@gmail.com"
                    className="text-white font-semibold hover:text-[#D4AF37] transition-colors"
                  >
                    CoachOmniMuscle@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div>
            {status === "success" ? (
              <div
                data-testid="contact-success"
                className="bg-[#0F0F0F] border-2 border-[#D4AF37] p-10 text-center"
              >
                <CheckCircle2 size={56} className="text-[#D4AF37] mx-auto mb-4" />
                <h3 className="font-display text-3xl text-white mb-3">
                  Message Locked In
                </h3>
                <p className="text-neutral-400 mb-6">
                  I've received your message. Expect a personal reply within 48
                  hours.
                </p>
                <button
                  data-testid="contact-send-another"
                  onClick={() => setStatus("idle")}
                  className="btn-omni btn-outline-gold"
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form
                data-testid="contact-form"
                onSubmit={submit}
                className="bg-[#0F0F0F] border border-zinc-800 p-8 space-y-5"
              >
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-2">
                    Name
                  </label>
                  <input
                    data-testid="contact-input-name"
                    required
                    className="input-omni"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-2">
                    Email
                  </label>
                  <input
                    data-testid="contact-input-email"
                    id="contact-form-email"
                    name="contact_email"
                    required
                    type="email"
                    className="input-omni"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    autoComplete="email"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-2">
                    Subject
                  </label>
                  <select
                    data-testid="contact-input-subject"
                    className="input-omni"
                    value={form.subject}
                    onChange={update("subject")}
                  >
                    <option>Coaching Inquiry</option>
                    <option>Stage Prep</option>
                    <option>Sponsorship / Collab</option>
                    <option>Supplement Stack Question</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-2">
                    Message
                  </label>
                  <textarea
                    data-testid="contact-input-message"
                    required
                    rows={5}
                    className="input-omni"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell me about your goals, current stats, training history…"
                  />
                </div>

                {status === "error" && (
                  <div
                    data-testid="contact-error"
                    className="text-sm text-[#EF4444] border border-[#DC2626] bg-[#DC2626]/10 px-4 py-3"
                  >
                    {error}
                  </div>
                )}

                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={status === "loading"}
                  className="btn-omni btn-red w-full disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Send Message <Send size={16} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
